import { Box, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { Color } from 'shared/utils/colors';
import { formatDonutSliceTooltipValue } from '../formatters/buildDonutLegendItems';
import { getGaugeGradientFill } from '../theme/chartGradients';
import {
  CHART_DONUT_STYLE,
  CHART_FONT_FAMILY,
  getChartTooltipSx,
} from '../theme/muiChartSlotProps';
import {
  CHART_DONUT_INNER_RADIUS,
  CHART_GAUGE_VALUE_MAX,
  CHART_GAUGE_VALUE_MIN,
  CHART_TRIPLET_GAUGE_SIZE,
  getChartDonutMargin,
} from '../tokens/muiChartLayout';
import { formatChartPercent } from '../utils/formatChartPercent';
import { ChartSvgDefs } from './ChartSvgDefs';

const GAUGE_VALUE_SLICE_ID = 'value';
const GAUGE_REMAINDER_SLICE_ID = 'remainder';

type TripletGaugePartProps = Readonly<{
  chartId: string;
  gaugeIndex: number;
  value: number;
  color: string;
  label: string;
  size?: number;
  variant?: 'dashboard' | 'details';
}>;

export function TripletGaugePart({
  chartId,
  gaugeIndex,
  value,
  color,
  label,
  size = CHART_TRIPLET_GAUGE_SIZE,
  variant = 'details',
}: TripletGaugePartProps): ReactElement {
  const intl = useIntl();
  const clamped = Math.min(
    CHART_GAUGE_VALUE_MAX,
    Math.max(CHART_GAUGE_VALUE_MIN, value)
  );
  const remainder = CHART_GAUGE_VALUE_MAX - clamped;
  const fill = getGaugeGradientFill(chartId, gaugeIndex);
  const isDashboard = variant === 'dashboard';

  const pieData = [
    ...(clamped > 0
      ? [
          {
            id: GAUGE_VALUE_SLICE_ID,
            value: clamped,
            label,
            color: fill,
          },
        ]
      : []),
    ...(remainder > 0
      ? [
          {
            id: GAUGE_REMAINDER_SLICE_ID,
            value: remainder,
            label: '',
            color: Color.solidGray,
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        flex: `0 0 ${size}px`,
        width: size,
        height: size,
        overflow: 'visible',
      }}
    >
      <ChartSvgDefs
        chartId={chartId}
        colors={[]}
        gaugeStops={[{ index: gaugeIndex, baseColor: color }]}
      />
      <PieChart
        series={[
          {
            data: pieData,
            innerRadius: CHART_DONUT_INNER_RADIUS,
            paddingAngle: isDashboard
              ? CHART_DONUT_STYLE.dashboardPaddingAngle
              : CHART_DONUT_STYLE.paddingAngle,
            cornerRadius: isDashboard
              ? CHART_DONUT_STYLE.dashboardCornerRadius
              : CHART_DONUT_STYLE.cornerRadius,
            arcLabelMinAngle: CHART_DONUT_STYLE.arcLabelMinAngle,
            valueFormatter: (item) =>
              item.id === GAUGE_REMAINDER_SLICE_ID
                ? ''
                : formatDonutSliceTooltipValue(
                    intl,
                    item.value,
                    CHART_GAUGE_VALUE_MAX
                  ),
            highlightScope: { highlight: 'item', fade: 'global' },
            highlighted: {
              additionalRadius: isDashboard
                ? CHART_DONUT_STYLE.dashboardHighlightAdditionalRadius
                : CHART_DONUT_STYLE.highlightAdditionalRadius,
            },
          },
        ]}
        width={size}
        height={size}
        margin={getChartDonutMargin(variant)}
        hideLegend
        slotProps={{ tooltip: { sx: getChartTooltipSx() } }}
        sx={{ overflow: 'visible' }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          textAlign: 'center',
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontFamily: CHART_FONT_FAMILY,
            fontWeight: 700,
            lineHeight: 1.1,
          }}
        >
          {formatChartPercent(clamped)}
        </Typography>
      </Box>
    </Box>
  );
}
